import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Lock, Mail, Phone, Star, User } from "lucide-react";
import { extractPreAuthToken, teacherApi } from "../../features/teacher/teacherApi";
import { setTeacherSignupContext } from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { TEACHER_YEAR_OPTIONS } from "../../utils/teacherYears";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  password: "",
  year: "",
  experience: "",
};

const SIGNUP_YEAR_OPTIONS = TEACHER_YEAR_OPTIONS.map((option) => String(option.id));
const SIGNUP_EXPERIENCE_OPTIONS = ["1", "2", "3", "4", "5"];
const SIGNUP_FIELD_ERROR_ALIASES = {
  name: ["name", "full_name", "fullName"],
  email: ["email", "email_address"],
  phone: ["phone", "phone_number", "mobile", "mobile_number"],
  password: ["password"],
  year: ["year", "year_id"],
  experience: ["experience"],
};

const normalizeSignupFieldErrors = (fieldErrors = {}) =>
  Object.entries(SIGNUP_FIELD_ERROR_ALIASES).reduce(
    (result, [fieldName, aliases]) => {
      const matchedAlias = aliases.find((alias) =>
        String(fieldErrors?.[alias] || "").trim()
      );

      if (matchedAlias) {
        result[fieldName] = String(fieldErrors[matchedAlias] || "").trim();
      }

      return result;
    },
    {}
  );

export default function TeacherSignup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const signupMutation = useMutation({
    mutationFn: teacherApi.signup,
    onSuccess: (payload) => {
      const preAuthToken = extractPreAuthToken(payload);

      if (!preAuthToken) {
        setGeneralMessage(
          "Signup succeeded but the backend did not return a pre_auth_token."
        );
        return;
      }

      dispatch(
        setTeacherSignupContext({
          preAuthToken,
          email: formData.email.trim(),
        })
      );

      navigate("/teacherverify-otp", {
        replace: true,
        state: {
          preAuthToken,
          email: formData.email.trim(),
        },
      });
    },
    onError: (error) => {
      const nextFieldErrors = normalizeSignupFieldErrors(error.fieldErrors || {});
      const hasFieldErrors = Object.keys(nextFieldErrors).length > 0;

      setFieldErrors(nextFieldErrors);
      setGeneralMessage(
        hasFieldErrors ? "" : getTeacherAuthErrorMessage(error, "signup")
      );
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const nextErrors = {};
    ["name", "email", "phone", "password", "year", "experience"].forEach(
      (fieldName) => {
        if (!String(formData[fieldName] || "").trim()) {
          nextErrors[fieldName] = `${fieldName.charAt(0).toUpperCase()}${fieldName.slice(
            1
          )} is required.`;
        }
      }
    );

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    if (formData.password.length < 8) {
      setFieldErrors({
        password: "Password must be at least 8 characters long.",
      });
      return;
    }

    const normalizedYear = formData.year.trim();
    const normalizedExperience = formData.experience.trim();

    if (!SIGNUP_YEAR_OPTIONS.includes(normalizedYear)) {
      setFieldErrors({
        year: "Please select a year between 4 and 9.",
      });
      return;
    }

    if (!SIGNUP_EXPERIENCE_OPTIONS.includes(normalizedExperience)) {
      setFieldErrors({
        experience: "Please select experience between 1 and 5.",
      });
      return;
    }

    signupMutation.mutate({
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      password: formData.password,
      year: normalizedYear,
      experience: normalizedExperience,
    });
  };

  return (
    <TeacherAuthLayout
      title="Teacher Signup"
      subtitle="Create your account and continue to OTP verification."
      cardClassName="teacher-auth-card-compact"
      wide
      footer={
        <p className="teacher-auth-footer-text">
          Already registered?{" "}
          <Link className="teacher-auth-link" to="/teacherlogin">
            Back to login
          </Link>
        </p>
      }
    >
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <div className="teacher-auth-grid">
          <TeacherAuthField
            id="teacher-signup-name"
            label="Name"
            icon={User}
            name="name"
            required
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            error={fieldErrors.name}
          />

          <TeacherAuthField
            id="teacher-signup-email"
            label="Email"
            icon={Mail}
            name="email"
            required
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            error={fieldErrors.email}
          />

          <TeacherAuthField
            id="teacher-signup-phone"
            label="Phone"
            icon={Phone}
            name="phone"
            required
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
            error={fieldErrors.phone}
          />

          <TeacherAuthField
            id="teacher-signup-password"
            label="Password"
            icon={Lock}
            name="password"
            required
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            error={fieldErrors.password}
            note="Password must be at least 8 characters."
            trailingAction={
              <button
                className="teacher-auth-toggle"
                type="button"
                onClick={() => setShowPassword((previous) => !previous)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
          />

          <TeacherAuthField
            id="teacher-signup-year"
            label="Year"
            icon={Star}
            name="year"
            required
            type="text"
            list="teacher-signup-year-options"
            autoComplete="off"
            inputMode="numeric"
            pattern="[4-9]"
            value={formData.year}
            onChange={handleChange}
            placeholder="Select or type a year from 4 to 9"
            error={fieldErrors.year}
            note="Available years: 4, 5, 6, 7, 8, 9."
          />

          <datalist id="teacher-signup-year-options">
            {SIGNUP_YEAR_OPTIONS.map((yearOption) => (
              <option key={yearOption} value={yearOption} />
            ))}
          </datalist>

          <TeacherAuthField
            id="teacher-signup-experience"
            label="Experience"
            icon={Star}
            name="experience"
            required
            type="text"
            list="teacher-signup-experience-options"
            autoComplete="off"
            inputMode="numeric"
            pattern="[1-5]"
            value={formData.experience}
            onChange={handleChange}
            placeholder="Select or type experience from 1 to 5"
            error={fieldErrors.experience}
          />

          <datalist id="teacher-signup-experience-options">
            {SIGNUP_EXPERIENCE_OPTIONS.map((experienceOption) => (
              <option key={experienceOption} value={experienceOption} />
            ))}
          </datalist>

        </div>

        <button className="teacher-auth-button" type="submit" disabled={signupMutation.isPending}>
          {signupMutation.isPending ? "Creating account..." : "Continue to OTP"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
